import { useEffect, useMemo, useState } from "react";
import { X, Check } from "lucide-react";
import { waLink, openWhatsApp } from "@/siteConfig";
import { Chip, LineInput, Eyebrow } from "@/components/bits";
import { useLang } from "@/i18n";

const TIMES = ["11:00", "14:00", "16:30"];
const DOORS = ["door.owner", "door.developer", "door.acquirer", "door.buyer"];
const DOOR_PROPS = { Owner: "door.owner", Developer: "door.developer", Acquirer: "door.acquirer", Buyer: "door.buyer" };

const toLocalISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

export const BookingModal = ({ door, onClose }) => {
  const { t, tf, lang } = useLang();
  const locale = lang === "or" ? "or-IN" : "en-IN";

  const days = useMemo(() => {
    const out = [];
    const base = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      out.push(d);
    }
    return out;
  }, []);

  const [sourceDoor, setSourceDoor] = useState(DOOR_PROPS[door] || "door.owner");
  const [dateIdx, setDateIdx] = useState(null);
  const [time, setTime] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (door) {
      setSourceDoor(DOOR_PROPS[door] || "door.owner");
      setDateIdx(null);
      setTime(null);
      setName("");
      setPhone("");
      setDone(false);
      setError("");
    }
  }, [door]);

  if (!door) return null;

  const selected = dateIdx !== null ? days[dateIdx] : null;
  const iso = selected ? toLocalISO(selected) : "";
  const waSummary = [
    t("wa.bk.title"),
    `${t("wa.door")}: ${t(sourceDoor)}`,
    `${t("wa.date")}: ${iso}`,
    `${t("wa.time")}: ${time} IST`,
    `${t("wa.name")}: ${name}`,
    `${t("wa.phone")}: ${phone}`,
  ].join("\n");

  const confirm = () => {
    if (dateIdx === null || !time || !name.trim() || !phone.trim()) {
      setError(t("bk.error"));
      return;
    }
    setError("");
    openWhatsApp(waSummary);
    setDone(true);
  };

  return (
    <div
      data-testid="booking-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
    >
      <div
        data-testid="booking-modal-backdrop"
        className="absolute inset-0 bg-[#0E1613]/85 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-[#131F1A] border border-[#1A2A24] w-full sm:max-w-xl max-h-[92vh] overflow-y-auto p-7 md:p-10 m-0 sm:m-6">
        <button
          type="button"
          data-testid="booking-modal-close"
          onClick={onClose}
          className="absolute top-5 right-5 text-[#EDE7DA]/50 hover:text-[#B8894B] transition-colors duration-300"
        >
          <X className="w-5 h-5" />
        </button>

        <img src="/logo-mark.png" alt="Novare Estates" className="w-10 h-10 mb-6" />

        {done ? (
          <div data-testid="booking-confirmation" className="py-6">
            <span className="inline-flex items-center justify-center w-11 h-11 border border-[#B8894B]">
              <Check className="w-5 h-5 text-[#B8894B]" />
            </span>
            <h3 className="font-display text-2xl md:text-3xl text-[#EDE7DA] tracking-tight mt-6">
              {t("bk.done.h")}
            </h3>
            <p className="font-light text-sm md:text-base text-[#EDE7DA]/60 leading-relaxed mt-4">
              {tf("bk.done.body", { date: iso, time })}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <a
                data-testid="booking-whatsapp-confirm"
                href={waLink(waSummary)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono-label text-xs tracking-[0.2em] uppercase bg-[#B8894B] text-[#0E1613] px-7 py-3.5 hover:bg-[#EDE7DA] transition-colors duration-300 text-center"
              >
                {t("cta.waAgain")}
              </a>
              <button
                type="button"
                data-testid="booking-done-close"
                onClick={onClose}
                className="font-mono-label text-xs tracking-[0.2em] uppercase border border-[#1A2A24] text-[#EDE7DA]/70 px-7 py-3.5 hover:border-[#B8894B] hover:text-[#B8894B] transition-colors duration-300"
              >
                {t("cta.close")}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <Eyebrow>{t("bk.eyebrow")}</Eyebrow>
            <h3 className="font-display text-2xl md:text-3xl text-[#EDE7DA] tracking-tight mt-4">
              {t("bk.h")}
            </h3>

            <div className="mt-8">
              <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#EDE7DA]/45">
                {t("bk.doorLabel")}
              </span>
              <div className="flex flex-wrap gap-2.5 mt-3">
                {DOORS.map((k) => (
                  <Chip
                    key={k}
                    testId={`booking-door-${k.split(".").pop()}`}
                    active={sourceDoor === k}
                    onClick={() => setSourceDoor(k)}
                  >
                    {t(k)}
                  </Chip>
                ))}
              </div>
            </div>

            <div className="mt-7">
              <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#EDE7DA]/45">
                {t("bk.daysLabel")}
              </span>
              <div
                data-testid="booking-date-strip"
                className="flex gap-2.5 mt-3 overflow-x-auto pb-2"
              >
                {days.map((d, i) => (
                  <button
                    type="button"
                    key={toLocalISO(d)}
                    data-testid={`booking-date-${i}`}
                    onClick={() => setDateIdx(i)}
                    className={`shrink-0 border px-4 py-2.5 text-center transition-colors duration-300 ${
                      dateIdx === i
                        ? "border-[#B8894B] text-[#B8894B] bg-[#1A2A24]"
                        : "border-[#1A2A24] text-[#EDE7DA]/60 hover:border-[#B8894B]/60 hover:text-[#EDE7DA]"
                    }`}
                  >
                    <span className="block font-mono-label text-[9px] tracking-[0.2em] uppercase">
                      {d.toLocaleDateString(locale, { weekday: "short" })}
                    </span>
                    <span className="block font-display text-lg mt-0.5">
                      {d.getDate()}
                    </span>
                    <span className="block font-mono-label text-[9px] tracking-[0.2em] uppercase">
                      {d.toLocaleDateString(locale, { month: "short" })}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-7">
              <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#EDE7DA]/45">
                {t("bk.timeLabel")}
              </span>
              <div className="flex flex-wrap gap-2.5 mt-3">
                {TIMES.map((tm) => (
                  <Chip
                    key={tm}
                    testId={`booking-time-${tm.replace(":", "")}`}
                    active={time === tm}
                    onClick={() => setTime(tm)}
                  >
                    {tm}
                  </Chip>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-7 mt-8">
              <LineInput
                label={t("bk.nameLabel")}
                testId="booking-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("ph.name")}
              />
              <LineInput
                label={t("bk.phoneLabel")}
                testId="booking-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91"
              />
            </div>

            {error && (
              <p data-testid="booking-form-error" className="font-mono-label text-xs text-[#B8894B] mt-6">
                {error}
              </p>
            )}

            <button
              type="button"
              data-testid="booking-confirm-submit"
              onClick={confirm}
              className="w-full mt-9 font-mono-label text-xs tracking-[0.2em] uppercase bg-[#B8894B] text-[#0E1613] px-7 py-4 hover:bg-[#EDE7DA] transition-colors duration-300"
            >
              {t("bk.submit")}
            </button>
            <p className="font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#EDE7DA]/40 mt-4 text-center">
              {t("cta.waNote")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
